import { isDemoMode, db, storage } from '../firebase';
import { ref, deleteObject } from 'firebase/storage';
import type { LibraryItem, LibraryArticle, LibraryPDF, LibraryWeblink, Comment } from '../types';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  updateDoc,
  Timestamp,
  getDocsFromServer,
} from 'firebase/firestore';

const DEMO_LIBRARY_KEY = 'demo_library';
const DEMO_LIBRARY_COUNTER_KEY = 'demo_library_counter';
const DEMO_COMMENTS_KEY = 'demo_comments';

// Also read legacy demo_articles for backward compat
const LEGACY_DEMO_ARTICLES_KEY = 'demo_articles';

const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

// ── Demo helpers ──

const getDemoItems = (): LibraryItem[] => {
  // Try new key first, fall back to legacy articles
  const stored = localStorage.getItem(DEMO_LIBRARY_KEY);
  if (stored) {
    try {
      return JSON.parse(stored).map((item: any) => ({
        ...item,
        type: item.type || 'article',
        description: item.description || '',
        createdAt: new Date(item.createdAt),
        updatedAt: new Date(item.updatedAt),
      }));
    } catch {
      return [];
    }
  }

  // Migrate legacy articles
  const legacy = localStorage.getItem(LEGACY_DEMO_ARTICLES_KEY);
  if (legacy) {
    try {
      const articles = JSON.parse(legacy).map((a: any): LibraryArticle => ({
        ...a,
        type: 'article' as const,
        description: '',
        createdAt: new Date(a.createdAt),
        updatedAt: new Date(a.updatedAt),
      }));
      saveDemoItems(articles);
      return articles;
    } catch {
      return [];
    }
  }

  return [];
};

const saveDemoItems = (items: LibraryItem[]) => {
  localStorage.setItem(DEMO_LIBRARY_KEY, JSON.stringify(items));
};

const getNextDemoId = (): string => {
  const current = parseInt(localStorage.getItem(DEMO_LIBRARY_COUNTER_KEY) || '0', 10);
  const next = current + 1;
  localStorage.setItem(DEMO_LIBRARY_COUNTER_KEY, String(next));
  return `lib_${next}`;
};

const getDemoComments = (libraryItemId: string): Comment[] => {
  const stored = localStorage.getItem(`${DEMO_COMMENTS_KEY}_${libraryItemId}`);
  if (stored) {
    try {
      return JSON.parse(stored).map((c: any) => ({
        ...c,
        createdAt: new Date(c.createdAt),
        updatedAt: new Date(c.updatedAt),
      }));
    } catch {
      return [];
    }
  }
  return [];
};

const saveDemoComments = (libraryItemId: string, comments: Comment[]) => {
  localStorage.setItem(`${DEMO_COMMENTS_KEY}_${libraryItemId}`, JSON.stringify(comments));
};

// ── Firestore helpers ──

const firestoreToItem = (docId: string, data: any): LibraryItem => {
  const base = {
    id: docId,
    type: data.type || 'article',
    title: data.title,
    slug: data.slug,
    description: data.description || '',
    version: data.version || 1,
    changeSummary: data.changeSummary || '',
    linkedQualIds: data.linkedQualIds || [],
    skillTreeIds: data.skillTreeIds || [],
    createdAt: data.createdAt?.toDate?.() || (data.createdAt instanceof Date ? data.createdAt : new Date()),
    updatedAt: data.updatedAt?.toDate?.() || (data.updatedAt instanceof Date ? data.updatedAt : new Date()),
    createdBy: data.createdBy || '',
    createdByName: data.createdByName || '',
    updatedBy: data.updatedBy || '',
    updatedByName: data.updatedByName || '',
    deletedAt: data.deletedAt?.toDate?.() || (data.deletedAt instanceof Date ? data.deletedAt : null),
    deletedBy: data.deletedBy || null,
  };

  switch (base.type) {
    case 'pdf':
      return { ...base, type: 'pdf', fileUrl: data.fileUrl || '' } as LibraryPDF;
    case 'weblink':
      return {
        ...base,
        type: 'weblink',
        url: data.url || '',
        screenshotUrl: data.screenshotUrl || '',
      } as LibraryWeblink;
    case 'article':
    default:
      return { ...base, type: 'article', body: data.body || '' } as LibraryArticle;
  }
};

// ── Service ──

export const libraryService = {
  // ── CRUD ──

  async getAll(includeDeleted = false): Promise<LibraryItem[]> {
    if (isDemoMode) {
      const items = getDemoItems();
      return includeDeleted ? items : items.filter((i) => !i.deletedAt);
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const articlesRef = collection(db, 'articles');
      const snapshot = await getDocsFromServer(articlesRef);
      console.log('[getAll] Raw snapshot:', snapshot.docs.length, 'docs, fromCache:', snapshot.metadata.fromCache);

      if (!includeDeleted) {
        // Filter on raw Firestore data BEFORE parsing to avoid any conversion issues
        const activeDocs = snapshot.docs.filter((d) => {
          const raw = d.data();
          if (raw.deletedAt) {
            console.log('[getAll] Excluding soft-deleted:', d.id, raw.title, 'deletedAt:', raw.deletedAt);
            return false;
          }
          return true;
        });
        console.log('[getAll] After deletedAt filter:', activeDocs.length, 'active items');
        return activeDocs.map((d) => firestoreToItem(d.id, d.data()));
      }

      return snapshot.docs.map((d) => firestoreToItem(d.id, d.data()));
    } catch (error) {
      console.error('Error getting library items:', error);
      throw error;
    }
  },

  async getById(id: string): Promise<LibraryItem | null> {
    if (isDemoMode) {
      return getDemoItems().find((i) => i.id === id) || null;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const docRef = doc(db, 'articles', id);
      const snapshot = await getDoc(docRef);
      if (!snapshot.exists()) return null;
      return firestoreToItem(snapshot.id, snapshot.data());
    } catch (error) {
      console.error('Error getting library item:', error);
      throw error;
    }
  },

  async getBySlug(slug: string): Promise<LibraryItem | null> {
    if (isDemoMode) {
      return getDemoItems().find((i) => i.slug === slug) || null;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, where('slug', '==', slug));
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      const d = snapshot.docs[0];
      return firestoreToItem(d.id, d.data());
    } catch (error) {
      console.error('Error getting library item by slug:', error);
      throw error;
    }
  },

  async save(
    item: Partial<LibraryItem> & { title: string; type: LibraryItem['type'] },
    changeSummary: string,
    userId: string,
    displayName: string
  ): Promise<LibraryItem> {
    const slug = generateSlug(item.title);
    const now = new Date();

    if (isDemoMode) {
      const items = getDemoItems();
      const existingIndex = items.findIndex((i) => i.id === item.id);

      if (existingIndex >= 0) {
        const existing = items[existingIndex];
        const updated = {
          ...existing,
          ...item,
          slug,
          version: existing.version + 1,
          changeSummary,
          updatedAt: now,
          updatedBy: userId,
          updatedByName: displayName,
          linkedQualIds: item.linkedQualIds || existing.linkedQualIds,
          skillTreeIds: item.skillTreeIds || existing.skillTreeIds || [],
        } as LibraryItem;
        items[existingIndex] = updated;
        saveDemoItems(items);
        return updated;
      } else {
        const base = {
          id: item.id || getNextDemoId(),
          title: item.title,
          slug,
          description: item.description || '',
          version: 1,
          changeSummary,
          linkedQualIds: item.linkedQualIds || [],
          skillTreeIds: item.skillTreeIds || [],
          createdAt: now,
          updatedAt: now,
          createdBy: userId,
          createdByName: displayName,
          updatedBy: userId,
          updatedByName: displayName,
          deletedAt: null,
          deletedBy: null,
        };

        let newItem: LibraryItem;
        switch (item.type) {
          case 'pdf':
            newItem = { ...base, type: 'pdf', fileUrl: (item as any).fileUrl || '' };
            break;
          case 'weblink':
            newItem = {
              ...base,
              type: 'weblink',
              url: (item as any).url || '',
              screenshotUrl: (item as any).screenshotUrl || '',
            };
            break;
          case 'article':
          default:
            newItem = { ...base, type: 'article', body: (item as any).body || '' };
            break;
        }

        items.push(newItem);
        saveDemoItems(items);
        return newItem;
      }
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      let docId = item.id;
      let version = 1;
      let existingItem: LibraryItem | null = null;

      if (docId) {
        existingItem = await this.getById(docId);
        if (existingItem) {
          version = existingItem.version + 1;
        }
      } else {
        docId = `lib_${Date.now()}`;
      }

      const docRef = doc(db, 'articles', docId);

      const baseData: any = {
        type: item.type,
        title: item.title,
        slug,
        description: item.description || '',
        version,
        changeSummary,
        linkedQualIds: item.linkedQualIds || [],
        skillTreeIds: item.skillTreeIds || [],
        createdAt: existingItem ? existingItem.createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: existingItem?.createdBy || userId,
        createdByName: existingItem?.createdByName || displayName,
        updatedBy: userId,
        updatedByName: displayName,
      };

      // Type-specific fields
      if (item.type === 'article') {
        baseData.body = (item as any).body || '';
      } else if (item.type === 'pdf') {
        baseData.fileUrl = (item as any).fileUrl || '';
      } else if (item.type === 'weblink') {
        baseData.url = (item as any).url || '';
        baseData.screenshotUrl = (item as any).screenshotUrl || '';
      }

      await setDoc(docRef, baseData);

      return firestoreToItem(docId, {
        ...baseData,
        createdAt: existingItem?.createdAt || now,
        updatedAt: now,
      });
    } catch (error) {
      console.error('Error saving library item:', error);
      throw error;
    }
  },

  async softDelete(id: string, userId: string): Promise<void> {
    if (isDemoMode) {
      const items = getDemoItems();
      const item = items.find((i) => i.id === id);
      if (item) {
        item.deletedAt = new Date();
        item.deletedBy = userId;
        saveDemoItems(items);
      }
      return;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const docRef = doc(db, 'articles', id);
      console.log('[softDelete] Writing deletedAt to Firestore for doc:', id);
      await updateDoc(docRef, { deletedAt: Timestamp.now(), deletedBy: userId });
      console.log('[softDelete] updateDoc resolved OK');

      // Verify the write actually landed
      const { getDocFromServer } = await import('firebase/firestore');
      const verify = await getDocFromServer(docRef);
      const verifyData = verify.data();
      console.log('[softDelete] Verification read — deletedAt:', verifyData?.deletedAt, 'deletedBy:', verifyData?.deletedBy);
      if (!verifyData?.deletedAt) {
        throw new Error('Soft-delete wrote but verification read shows no deletedAt — possible permissions issue');
      }
    } catch (error) {
      console.error('Error soft-deleting library item:', error);
      throw error;
    }
  },

  async restore(id: string): Promise<void> {
    if (isDemoMode) {
      const items = getDemoItems();
      const item = items.find((i) => i.id === id);
      if (item) {
        item.deletedAt = null;
        item.deletedBy = null;
        saveDemoItems(items);
      }
      return;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const docRef = doc(db, 'articles', id);
      await updateDoc(docRef, { deletedAt: null, deletedBy: null });
    } catch (error) {
      console.error('Error restoring library item:', error);
      throw error;
    }
  },

  async permanentDelete(id: string): Promise<void> {
    if (isDemoMode) {
      const items = getDemoItems().filter((i) => i.id !== id);
      saveDemoItems(items);
      return;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const docRef = doc(db, 'articles', id);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        if (data.fileUrl && storage && data.fileUrl.includes('firebasestorage.googleapis.com')) {
          try {
            await deleteObject(ref(storage, data.fileUrl));
          } catch (err) {
            console.warn('Could not delete storage file:', err);
          }
        }
      }
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error permanently deleting library item:', error);
      throw error;
    }
  },

  // ── Comments ──

  async getComments(libraryItemId: string): Promise<Comment[]> {
    if (isDemoMode) {
      return getDemoComments(libraryItemId);
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const commentsRef = collection(db, 'articles', libraryItemId, 'comments');
      const q = query(commentsRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map((d) => {
          const data = d.data();
          return {
            id: d.id,
            libraryItemId,
            userId: data.userId,
            userName: data.userName,
            body: data.body,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            deletedAt: data.deletedAt?.toDate() || null,
            deletedBy: data.deletedBy || null,
          };
        })
        .filter((c) => !c.deletedAt);
    } catch (error) {
      console.error('Error getting comments:', error);
      throw error;
    }
  },

  async addComment(
    libraryItemId: string,
    userId: string,
    userName: string,
    body: string
  ): Promise<Comment> {
    const now = new Date();
    const commentId = `comment_${Date.now()}`;

    if (isDemoMode) {
      const comments = getDemoComments(libraryItemId);
      const comment: Comment = {
        id: commentId,
        libraryItemId,
        userId,
        userName,
        body,
        createdAt: now,
        updatedAt: now,
      };
      comments.push(comment);
      saveDemoComments(libraryItemId, comments);
      return comment;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const commentRef = doc(db, 'articles', libraryItemId, 'comments', commentId);
      await setDoc(commentRef, {
        userId,
        userName,
        body,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { id: commentId, libraryItemId, userId, userName, body, createdAt: now, updatedAt: now };
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  },

  async deleteComment(libraryItemId: string, commentId: string, deletedBy: string): Promise<void> {
    if (isDemoMode) {
      const comments = getDemoComments(libraryItemId);
      const c = comments.find((c) => c.id === commentId);
      if (c) {
        c.deletedAt = new Date();
        c.deletedBy = deletedBy;
        saveDemoComments(libraryItemId, comments);
      }
      return;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      await updateDoc(doc(db, 'articles', libraryItemId, 'comments', commentId), {
        deletedAt: Timestamp.now(),
        deletedBy,
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  },
};

// Keep backward compat export
export const articleService = libraryService;
